import React from "react";

export default function QuotationManagementSection() {
  const statuses = [
    "New RFQ",
    "Under Review",
    "Estimation",
    "Submitted",
    "Customer Review",
    "Selected",
    "Not Selected",
    "Expired",
  ];

  const checklistItems = [
    "View New RFQs",
    "Download CAD Files",
    "View Technical Drawings",
    "Review Requirements",
    "Ask Clarifications",
    "Material Costs",
    "Labour Costs",
    "Machine Costs",
    "Finishing Costs",
    "Delivery Charges",
    "Define Delivery Date",
    "Track Quotation Status",
  ];

  const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f7941d" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.3l2.3 2.3 4.5-4.8" />
    </svg>
  );

  const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0b1330" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );

  return (
    <section className="qm-section">
      <style>{`
        .qm-section {
          width: 100%;
          background: linear-gradient(180deg, #f7f9fd 0%, #eef1f8 100%);
          font-family: 'Inter', 'Segoe UI', sans-serif;
          box-sizing: border-box;
          padding: 72px 0;
        }
        .qm-wrap {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 32px;
          display: grid;
          grid-template-columns: 0.85fr 1.15fr;
          gap: 64px;
          align-items: center;
          box-sizing: border-box;
        }

        /* LEFT column */
        .qm-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 9px 18px;
          border-radius: 999px;
          background: #fdecd3;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #0b1330;
        }
        .qm-eyebrow-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #f7941d;
          flex-shrink: 0;
        }
        .qm-heading {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 800;
          font-size: clamp(1.9rem, 4vw, 2.9rem);
          line-height: 1.16;
          letter-spacing: -0.02em;
          color: #0b1330;
          margin-top: 22px;
        }
        .qm-accent { color: #f7941d; }
        .qm-paragraph {
          margin-top: 22px;
          max-width: 480px;
          font-size: clamp(0.95rem, 2vw, 1.05rem);
          line-height: 1.7;
          color: #5a6280;
        }
        .qm-status-grid {
          margin-top: 30px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          max-width: 520px;
        }
        .qm-status-pill {
          padding: 9px 18px;
          border-radius: 999px;
          border: 1px solid #dfe3ee;
          background: #ffffff;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 0.88rem;
          font-weight: 700;
          color: #0b1330;
          white-space: nowrap;
        }

        /* RIGHT column: quotation builder card */
        .qm-card {
          background: #ffffff;
          border-radius: 24px;
          box-shadow: 0 30px 60px -30px rgba(11,19,48,0.25);
          padding: 32px;
          box-sizing: border-box;
          position: relative;
        }
        .qm-card-eyebrow {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: #8b93ad;
        }
        .qm-card-header-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .qm-card-title {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 1.35rem;
          color: #0b1330;
          margin: 6px 0 0;
        }
        .qm-match-badge {
          background: linear-gradient(135deg, #f7941d, #ef7f0e);
          color: #ffffff;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 0.85rem;
          padding: 8px 16px;
          border-radius: 999px;
          white-space: nowrap;
        }
        .qm-checklist {
          margin-top: 26px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        .qm-check-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 14px;
          background: #f4f6fb;
          border: 1px solid #eceff7;
          font-size: 0.95rem;
          font-weight: 600;
          color: #23283a;
          box-sizing: border-box;
        }
        .qm-total-bar {
          margin-top: 26px;
          background: #0b1330;
          border-radius: 16px;
          padding: 22px 26px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .qm-total-label {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: #93a0c9;
        }
        .qm-total-value {
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-size: 1.7rem;
          font-weight: 800;
          color: #ffffff;
          margin-top: 4px;
        }
        .qm-submit-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #f7941d;
          color: #0b1330;
          font-family: 'Sora', 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          padding: 14px 26px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          white-space: nowrap;
        }

        /* Tablet: stack the two columns */
        @media (max-width: 960px) {
          .qm-wrap {
            grid-template-columns: 1fr;
            gap: 40px;
          }
          .qm-paragraph, .qm-status-grid { max-width: 100%; }
        }

        /* Small tablet / large phone: checklist to single column */
        @media (max-width: 640px) {
          .qm-section { padding: 48px 0; }
          .qm-wrap { padding: 0 20px; }
          .qm-card { padding: 24px; border-radius: 18px; }
          .qm-checklist { grid-template-columns: 1fr; gap: 12px; }
          .qm-total-bar { flex-direction: column; align-items: stretch; }
          .qm-submit-btn { width: 100%; justify-content: center; }
        }

        /* Small phones: tighten pills and card header */
        @media (max-width: 400px) {
          .qm-status-pill { font-size: 0.8rem; padding: 8px 14px; }
          .qm-card-title { font-size: 1.15rem; }
          .qm-total-value { font-size: 1.4rem; }
        }
      `}</style>

      <div className="qm-wrap">
        {/* LEFT */}
        <div>
          <span className="qm-eyebrow">
            <span className="qm-eyebrow-dot" />
            QUOTATION MANAGEMENT
          </span>

          <h2 className="qm-heading">
            From RFQ to quotation —<br />
            <span className="qm-accent">completely digital.</span>
          </h2>

          <p className="qm-paragraph">
            Facility teams manage every quotation from one dashboard. Review
            CAD files, add cost lines and submit competitive quotes in
            minutes.
          </p>

          <div className="qm-status-grid">
            {statuses.map((s, i) => (
              <span className="qm-status-pill" key={i}>
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div className="qm-card">
          <div className="qm-card-header-row">
            <div>
              <div className="qm-card-eyebrow">QUOTATION BUILDER</div>
              <h3 className="qm-card-title">RFQ #4821 · Prototype XYZ</h3>
            </div>
            <span className="qm-match-badge">Match 96%</span>
          </div>

          <div className="qm-checklist">
            {checklistItems.map((item, i) => (
              <div className="qm-check-item" key={i}>
                <CheckIcon />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="qm-total-bar">
            <div>
              <div className="qm-total-label">ESTIMATED TOTAL</div>
              <div className="qm-total-value">₹ 4,82,000</div>
            </div>
            <button className="qm-submit-btn">
              Submit Quotation
              <ArrowIcon />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}